import { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button } from 'src/components/ui';
import { getTestId } from '../../hooks/useTestId';
import { Trans } from '@lingui/react/macro';
import Loader from '../../components/Loader';
import Notification from '../../components/Notification';
import { DataRemark } from '../../api/DataRemarksApi';
import { isGeneralRemark } from '../useDataRemarks';
import Icon from 'src/components/Icon';

type ResolveDataRemarkModalProps = {
  show: boolean;
  onClose: () => void;
  onSubmit: (payload: { resolutionNotes: string }) => void;
  remark: DataRemark | null;
  modalLoading?: boolean;
  error?: string | null;
};

function ResolveDataRemarkModal({ show, onClose, onSubmit, remark, modalLoading = false, error }: ResolveDataRemarkModalProps) {
  const [resolutionNotes, setResolutionNotes] = useState('');

  useEffect(() => {
    if (!show) {
      setResolutionNotes('');
    }
  }, [show]);

  const handleSubmit = () => {
    onSubmit({ resolutionNotes: resolutionNotes.trim() });
  };

  return (
    <Modal isOpen={show} toggle={onClose} size="lg" className="dataremark-modal-dialog" backdrop={false}>
      {modalLoading && (
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.1)',
            zIndex: 20,
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader spinner />
        </div>
      )}
      <ModalHeader toggle={onClose}>
        <Trans>Mark Flag as Resolved</Trans>
      </ModalHeader>
      <ModalBody>
        {error != null && <Notification color="danger">{error}</Notification>}
        {remark && (
          <div className="mb-3 p-3 bg-light rounded">
            <div className="mb-2 flex items-center gap-1">
              <small className="small-label">
                <Trans>Field:</Trans>
              </small>
              <span className="ml-2 small">
                {isGeneralRemark(remark) ? <Trans>Other</Trans> : Array.isArray(remark.fieldName) ? remark.fieldName.join(', ') : remark.fieldName}
              </span>
            </div>
            <div className="mb-2">
              <small className="small-label">
                <Trans>Issue:</Trans>
              </small>
              <p className="mb-0 ml-2 small text-muted">{remark.message}</p>
            </div>
            {remark.description && (
              <div className="mb-2">
                <small className="small-label">
                  <Trans>Details:</Trans>
                </small>
                <p className="mb-0 ml-2 small text-muted">{remark.description}</p>
              </div>
            )}
          </div>
        )}

        <Form>
          <FormGroup>
            <Label for="resolutionNotes" className="small-label">
              <Trans>Resolution Notes</Trans> <span className="text-muted font-normal">(optional)</span>
            </Label>
            <Input
              type="textarea"
              id="resolutionNotes"
              value={resolutionNotes}
              onChange={e => setResolutionNotes(e.target.value)}
              placeholder="Explain how this issue was resolved or add any final notes..."
              rows={4}
              {...getTestId('resolve-remark-modal', '.', 'resolution-notes')}
            />
            <small className="text-muted">
              <Trans>Provide additional context about the resolution if needed</Trans>
            </small>
          </FormGroup>
        </Form>

        <div className="alert alert-info mb-0 flex items-center gap-1">
          <Icon icon="Info" className="mr-2" />
          <small>
            <Trans>Once marked as resolved, this flag will be archived. You can still view resolved flag in the list.</Trans>
          </small>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose} {...getTestId('resolve-remark-modal', '.', 'cancel')}>
          <Trans>Cancel</Trans>
        </Button>
        <Button className="flex items-center gap-1" color="success" onClick={handleSubmit} {...getTestId('resolve-remark-modal', '.', 'confirm')}>
          <Icon icon="Check" className="mr-2" />
          <Trans>Mark as Resolved</Trans>
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ResolveDataRemarkModal;
